import { useEffect, useState, useCallback, useRef } from "react";
import {
  RefreshCw, Truck, Package, AlertTriangle, ShoppingBag,
  Car, Bike, CircleDot,
} from "lucide-react";

function authHeaders(): Record<string, string> {
  const t = localStorage.getItem("ayrshire_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

// What the DRIVER earns for a delivery = delivery fee + shop fuel bonus + hub-funded
// top-up. This is the figure that reconciles with each driver's "earned today" total.
// The order's totalPence is the CUSTOMER's bill (food + fees) — a different number,
// which is why a delivery the driver earns £4 on was showing as the £5 order total.
function driverFeePence(o: { deliveryFeePence?: number; driverFuelSupplementPence?: number; hubFundedDriverPence?: number }) {
  return (o.deliveryFeePence ?? 0) + (o.driverFuelSupplementPence ?? 0) + (o.hubFundedDriverPence ?? 0);
}

function elapsed(iso: string | null | undefined): { mins: number; label: string } {
  if (!iso) return { mins: 0, label: "–" };
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return { mins: 0, label: "<1m" };
  if (mins < 60) return { mins, label: `${mins}m` };
  return { mins, label: `${Math.floor(mins / 60)}h ${mins % 60}m` };
}

function urgencyBorder(mins: number) {
  if (mins >= 20) return "border-red-500";
  if (mins >= 10) return "border-amber-500";
  return "border-slate-700";
}

function urgencyTimeColor(mins: number) {
  if (mins >= 20) return "text-red-400 font-bold";
  if (mins >= 10) return "text-amber-400";
  return "text-slate-500";
}

// ─── Types ─────────────────────────────────────────────────────────────────

type LiveOrder = {
  id: number;
  storeName: string | null;
  status: string;
  statusChangedAt: string | null;
  createdAt: string;
  totalPence: number;
  deliveryFeePence?: number;
  driverFuelSupplementPence?: number;
  hubFundedDriverPence?: number;
  paymentMethod: string;
  fulfilmentType: string;
  customerName: string | null;
  deliveryAddress: string | null;
  driverName: string | null;
  claimedAt: string | null;
  pickedUpAt: string | null;
};

type Offer = {
  driverName: string | null;
  status: string;
  offeredAt: string;
  expiresAt: string;
};

type DispatchingOrder = {
  orderId: number;
  storeName: string | null;
  dispatchStatus: string | null;
  deliveryAddress: string | null;
  customerName: string | null;
  totalPence: number;
  deliveryFeePence?: number;
  driverFuelSupplementPence?: number;
  hubFundedDriverPence?: number;
  createdAt: string;
  offers: Offer[];
};

type InTransitOrder = {
  orderId: number;
  storeName: string | null;
  deliveryAddress: string | null;
  customerName: string | null;
  totalPence: number;
  deliveryFeePence?: number;
  driverFuelSupplementPence?: number;
  hubFundedDriverPence?: number;
  createdAt: string;
  driverName: string | null;
  claimedAt: string | null;
  pickedUpAt: string | null;
};

type FailedOrder = {
  orderId: number;
  storeName: string | null;
  deliveryAddress: string | null;
  totalPence: number;
  deliveryFeePence?: number;
  driverFuelSupplementPence?: number;
  hubFundedDriverPence?: number;
  createdAt: string;
};

type HubDashData = {
  dispatching: DispatchingOrder[];
  inTransit: InTransitOrder[];
  failures: FailedOrder[];
};

type ItemRequest = {
  id: number;
  status: string;
  pickupAddress: string;
  dropoffAddress: string;
  quotedFeePence: number;
  createdAt: string;
  updatedAt: string;
  driverId: number | null;
  driverName: string | null;
};

type DeliveryRun = {id:number;status:string;storeName:string|null;driverName:string|null;dropCount:number;dropFeesTotalPence:number;fuelSupplementPence:number;createdAt:string};
function RunJobCard({run}:{run:DeliveryRun}){return <a href="/admin/delivery-runs" className="block rounded-lg border border-violet-700 bg-slate-900 px-3 py-2"><div className="flex justify-between gap-2"><span className="text-white text-sm font-semibold">Run #{run.id} · {run.dropCount} drops</span><span className="text-green-400 text-sm font-bold">{pence(run.dropFeesTotalPence+run.fuelSupplementPence)}</span></div><div className="text-slate-400 text-xs mt-1">{run.storeName}</div><div className="text-xs text-violet-300 mt-1">{run.status==='dispatching'?'Awaiting driver':run.driverName??'Driver assigned'} · {elapsed(run.createdAt).label}</div></a>;}

type Driver = {
  userId: number;
  userName: string | null;
  vehicleType: string | null;
  isAvailable: boolean;
  isOnline: boolean;
  todayDeliveryCount: number;
  todayEarningsPence: number;
  lastSeenAt: string | null;
};

type Summary = {
  activeDrivers: number;
  thisWeekPence: number;
  thisMonthPence: number;
};

// ─── Status config ──────────────────────────────────────────────────────────

const STATUS_CFG: Record<string, { label: string; dot: string; border: string; bg: string }> = {
  pending:          { label: "Pending",         dot: "bg-slate-400",  border: "border-slate-600",  bg: "bg-slate-900" },
  confirmed:        { label: "Confirmed",        dot: "bg-blue-400",   border: "border-blue-700",   bg: "bg-blue-950/50" },
  preparing:        { label: "Preparing",        dot: "bg-amber-400",  border: "border-amber-600",  bg: "bg-amber-950/40" },
  ready:            { label: "Ready",            dot: "bg-orange-400", border: "border-orange-600", bg: "bg-orange-950/40" },
  out_for_delivery: { label: "Out for Delivery", dot: "bg-green-400",  border: "border-green-600",  bg: "bg-green-950/40" },
};

const FOOD_STATUS_ORDER = ["pending", "confirmed", "preparing", "ready", "out_for_delivery"];

const ITEM_ACTIVE_STATUSES = new Set(["paid", "dispatching", "accepted", "picked_up"]);

// ─── Sub-components ─────────────────────────────────────────────────────────

function SectionHeader({ color, label, count }: { color: string; label: string; count: number }) {
  return (
    <div className={`flex items-center justify-between px-3 py-2 border-b ${color} mb-0`}>
      <span className="text-xs font-bold uppercase tracking-widest opacity-80">{label}</span>
      <span className="text-sm font-bold">{count}</span>
    </div>
  );
}

function VehicleIcon({ type }: { type: string | null }) {
  if (type === "car") return <Car size={13} className="text-slate-400" />;
  if (type === "van") return <Truck size={13} className="text-slate-400" />;
  return <Bike size={13} className="text-slate-400" />;
}

// ─── Panels ─────────────────────────────────────────────────────────────────

function FoodOrdersPanel({ orders }: { orders: LiveOrder[] }) {
  const grouped = FOOD_STATUS_ORDER.reduce<Record<string, LiveOrder[]>>((acc, s) => {
    acc[s] = orders.filter(o => o.status === s);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <SectionHeader color="border-slate-700 text-slate-300" label="Food Orders" count={orders.length} />
      <div className="flex-1 overflow-y-auto space-y-1 p-2">
        {orders.length === 0 && (
          <div className="flex items-center justify-center h-20 text-slate-600 text-sm">No live orders</div>
        )}
        {FOOD_STATUS_ORDER.map(status => {
          const group = grouped[status];
          if (!group || group.length === 0) return null;
          const cfg = STATUS_CFG[status];
          return (
            <div key={status}>
              {/* Status group header */}
              <div className="flex items-center gap-2 px-2 py-1 mt-1">
                <span className={`w-2 h-2 rounded-full ${cfg.dot} flex-shrink-0`} />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{cfg.label}</span>
                <span className="ml-auto text-xs text-slate-600">{group.length}</span>
              </div>
              {group.map(order => {
                const { mins, label: ageLabel } = elapsed(order.statusChangedAt ?? order.createdAt);
                return (
                  <div key={order.id} className={`rounded-lg border ${cfg.border} ${cfg.bg} px-3 py-2 mb-1`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-semibold">#{order.id}</span>
                          <span className="text-slate-300 text-sm truncate">{order.storeName ?? "—"}</span>
                        </div>
                        <div className="text-slate-400 text-xs truncate mt-0.5">
                          {order.customerName ?? "Guest"}
                          {order.fulfilmentType === "collection" && (
                            <span className="ml-2 text-indigo-400">[Collection]</span>
                          )}
                        </div>
                        {order.driverName && (
                          <div className="text-slate-500 text-xs mt-0.5 flex items-center gap-1">
                            <Truck size={10} />
                            <span>{order.driverName}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {order.fulfilmentType === "delivery" ? (
                          <div className="text-green-400 text-sm font-bold" title="Driver fee for this delivery">
                            {pence(driverFeePence(order))}<span className="text-[10px] font-normal text-slate-500 ml-1">fee</span>
                          </div>
                        ) : (
                          <div className="text-white text-sm font-bold">{pence(order.totalPence)}</div>
                        )}
                        <div className={`text-xs mt-0.5 ${urgencyTimeColor(mins)}`}>{ageLabel}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DispatchPanel({
  foodDispatching,
  itemDispatching,
  foodFailures,
  runs,
}: {
  foodDispatching: DispatchingOrder[];
  itemDispatching: ItemRequest[];
  foodFailures: FailedOrder[];
  runs: DeliveryRun[];
}) {
  const total = foodDispatching.length + itemDispatching.length + runs.length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <SectionHeader color="border-violet-800 text-violet-300" label="Dispatching" count={total} />
      <div className="flex-1 overflow-y-auto space-y-1.5 p-2">
        {total === 0 && (
          <div className="flex items-center justify-center h-20 text-slate-600 text-sm">Nothing dispatching</div>
        )}

        {runs.map(run=><RunJobCard key={`run-${run.id}`} run={run} />)}
        {/* Food orders waiting for driver */}
        {foodDispatching.map(order => {
          const { mins, label: ageLabel } = elapsed(order.createdAt);
          const activeOffers = order.offers.filter(o => o.status === "pending").length;
          const totalOffers = order.offers.length;
          return (
            <div key={`food-${order.orderId}`} className={`rounded-lg border ${urgencyBorder(mins)} bg-slate-900 px-3 py-2`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <ShoppingBag size={12} className="text-violet-400 flex-shrink-0" />
                    <span className="text-white text-sm font-semibold">Order #{order.orderId}</span>
                  </div>
                  <div className="text-slate-400 text-xs truncate mt-0.5">
                    {order.storeName} → {order.deliveryAddress ? order.deliveryAddress.split(",")[0] : "—"}
                  </div>
                  <div className="text-slate-500 text-xs mt-1">
                    {totalOffers > 0
                      ? `${activeOffers} pending offer${activeOffers !== 1 ? "s" : ""} / ${totalOffers} sent`
                      : "No offers yet"}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-green-400 text-sm font-bold" title="Driver fee for this delivery">
                    {pence(driverFeePence(order))}<span className="text-[10px] font-normal text-slate-500 ml-1">fee</span>
                  </div>
                  <div className={`text-xs mt-0.5 ${urgencyTimeColor(mins)}`}>{ageLabel}</div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Item deliveries waiting for driver */}
        {itemDispatching.map(req => {
          const { mins, label: ageLabel } = elapsed(req.createdAt);
          return (
            <div key={`item-${req.id}`} className={`rounded-lg border ${urgencyBorder(mins)} bg-slate-900 px-3 py-2`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Package size={12} className="text-cyan-400 flex-shrink-0" />
                    <span className="text-white text-sm font-semibold">Parcel #{req.id}</span>
                    <span className="text-xs text-cyan-600 bg-cyan-950/50 rounded px-1.5 py-0.5">Hub Dash</span>
                  </div>
                  <div className="text-slate-400 text-xs truncate mt-0.5">
                    {req.pickupAddress.split(",")[0]} → {req.dropoffAddress.split(",")[0]}
                  </div>
                  <div className="text-slate-500 text-xs mt-1">Awaiting driver</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-white text-sm font-bold">{pence(req.quotedFeePence)}</div>
                  <div className={`text-xs mt-0.5 ${urgencyTimeColor(mins)}`}>{ageLabel}</div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Failures */}
        {foodFailures.length > 0 && (
          <>
            <div className="flex items-center gap-2 px-1 pt-2">
              <AlertTriangle size={12} className="text-red-500" />
              <span className="text-xs font-semibold text-red-500 uppercase tracking-wider">Dispatch Failed</span>
              <span className="text-xs text-red-700">{foodFailures.length}</span>
            </div>
            {foodFailures.map(order => {
              const { label: ageLabel } = elapsed(order.createdAt);
              return (
                <div key={`fail-${order.orderId}`} className="rounded-lg border border-red-800 bg-red-950/30 px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="text-red-300 text-sm font-semibold">Order #{order.orderId}</span>
                      <div className="text-red-500 text-xs truncate mt-0.5">{order.storeName}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-red-200 text-sm font-bold" title="Driver fee for this delivery">
                        {pence(driverFeePence(order))}<span className="text-[10px] font-normal text-red-400 ml-1">fee</span>
                      </div>
                      <div className="text-red-600 text-xs">{ageLabel}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function InTransitPanel({
  foodInTransit,
  itemInTransit,
  runs,
}: {
  foodInTransit: InTransitOrder[];
  itemInTransit: ItemRequest[];
  runs: DeliveryRun[];
}) {
  const total = foodInTransit.length + itemInTransit.length + runs.length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <SectionHeader color="border-green-800 text-green-300" label="In Transit" count={total} />
      <div className="flex-1 overflow-y-auto space-y-1.5 p-2">
        {total === 0 && (
          <div className="flex items-center justify-center h-20 text-slate-600 text-sm">Nothing in transit</div>
        )}

        {runs.map(run=><RunJobCard key={`run-${run.id}`} run={run} />)}
        {foodInTransit.map(order => {
          const { mins: minsPickup, label: pickupLabel } = elapsed(order.pickedUpAt);
          const { label: claimedLabel } = elapsed(order.claimedAt);
          const isPickedUp = !!order.pickedUpAt;
          return (
            <div key={`food-${order.orderId}`} className={`rounded-lg border ${urgencyBorder(isPickedUp ? minsPickup : 0)} bg-slate-900 px-3 py-2`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <ShoppingBag size={12} className="text-green-400 flex-shrink-0" />
                    <span className="text-white text-sm font-semibold">#{order.orderId}</span>
                    <span className="text-slate-400 text-xs truncate">{order.storeName}</span>
                  </div>
                  {order.driverName && (
                    <div className="flex items-center gap-1 mt-1">
                      <Truck size={10} className="text-green-500" />
                      <span className="text-green-400 text-xs font-medium">{order.driverName}</span>
                    </div>
                  )}
                  <div className="text-slate-500 text-xs truncate mt-0.5">
                    → {order.deliveryAddress ? order.deliveryAddress.split(",")[0] : "—"}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-green-400 text-sm font-bold" title="Driver fee for this delivery">
                    {pence(driverFeePence(order))}<span className="text-[10px] font-normal text-slate-500 ml-1">fee</span>
                  </div>
                  <div className={`text-xs mt-0.5 ${urgencyTimeColor(isPickedUp ? minsPickup : 0)}`}>
                    {isPickedUp ? `${pickupLabel} since pickup` : `${claimedLabel} since claimed`}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {itemInTransit.map(req => {
          const isPickedUp = req.status === "picked_up";
          const { mins, label: ageLabel } = elapsed(isPickedUp ? req.updatedAt : req.createdAt);
          return (
            <div key={`item-${req.id}`} className={`rounded-lg border ${urgencyBorder(isPickedUp ? mins : 0)} bg-slate-900 px-3 py-2`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Package size={12} className="text-cyan-400 flex-shrink-0" />
                    <span className="text-white text-sm font-semibold">Parcel #{req.id}</span>
                    <span className="text-xs text-cyan-600 bg-cyan-950/50 rounded px-1.5 py-0.5 capitalize">{req.status.replace("_", " ")}</span>
                  </div>
                  {req.driverName && (
                    <div className="flex items-center gap-1 mt-1">
                      <Truck size={10} className="text-cyan-500" />
                      <span className="text-cyan-400 text-xs font-medium">{req.driverName}</span>
                    </div>
                  )}
                  <div className="text-slate-500 text-xs truncate mt-0.5">
                    {req.pickupAddress.split(",")[0]} → {req.dropoffAddress.split(",")[0]}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-white text-sm font-bold">{pence(req.quotedFeePence)}</div>
                  <div className={`text-xs mt-0.5 ${urgencyTimeColor(isPickedUp ? mins : 0)}`}>{ageLabel}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DriversPanel({ drivers }: { drivers: Driver[] }) {
  const online = drivers.filter(d => d.isOnline || d.isAvailable);
  const offline = drivers.filter(d => !d.isOnline && !d.isAvailable).sort((a, b) => {
    const seenA = a.lastSeenAt ? Date.parse(a.lastSeenAt) : NaN;
    const seenB = b.lastSeenAt ? Date.parse(b.lastSeenAt) : NaN;
    return (Number.isFinite(seenB) ? seenB : 0) - (Number.isFinite(seenA) ? seenA : 0);
  });

  function renderDriver(d: Driver, isOnline: boolean) {
    const { label: seenLabel } = elapsed(d.lastSeenAt);
    return (
      <div key={d.userId} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isOnline ? "bg-green-400" : "bg-slate-600"}`} />
              <span className="text-white text-sm font-semibold truncate">{d.userName ?? "Driver"}</span>
              <VehicleIcon type={d.vehicleType} />
            </div>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-slate-500 text-xs">{d.todayDeliveryCount} today</span>
              <span className="text-green-600 text-xs font-medium">{pence(d.todayEarningsPence)}</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className={`text-xs px-2 py-0.5 rounded-full font-medium ${isOnline ? "bg-green-900/60 text-green-400" : "bg-slate-800 text-slate-500"}`}>
              {isOnline ? (d.isAvailable ? "Available" : "Busy") : "Offline"}
            </div>
            {d.lastSeenAt && (
              <div className="text-slate-600 text-xs mt-1">{seenLabel} ago</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <SectionHeader color="border-slate-700 text-slate-300" label="Drivers" count={drivers.length} />
      <div className="flex-1 overflow-y-auto space-y-1.5 p-2">
        {online.length === 0 && offline.length === 0 && (
          <div className="flex items-center justify-center h-20 text-slate-600 text-sm">No drivers</div>
        )}

        {online.length > 0 && (
          <>
            <div className="text-xs font-semibold text-green-600 uppercase tracking-wider px-1 pt-1 pb-0.5">
              Online — {online.length}
            </div>
            {online.map(d => renderDriver(d, true))}
          </>
        )}

        {offline.length > 0 && (
          <>
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider px-1 pt-3 pb-0.5">
              Offline — {offline.length}
            </div>
            {offline.map(d => renderDriver(d, false))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Wallboard ─────────────────────────────────────────────────────────

const REFRESH_INTERVAL = 8;

export default function AdminWallboard() {
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);

  const [runs,setRuns] = useState<DeliveryRun[]>([]);
  const [liveOrders, setLiveOrders] = useState<LiveOrder[]>([]);
  const [hubDash, setHubDash] = useState<HubDashData>({ dispatching: [], inTransit: [], failures: [] });
  const [itemRequests, setItemRequests] = useState<ItemRequest[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [summary, setSummary] = useState<Summary>({ activeDrivers: 0, thisWeekPence: 0, thisMonthPence: 0 });
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(true);

  const countdownRef = useRef(REFRESH_INTERVAL);

  const load = useCallback(async () => {
    try {
      const [ordersRes, hubRes, itemRes, driversRes, summaryRes, runsRes] = await Promise.all([
        fetch("/api/admin/live-orders", { headers: authHeaders() }),
        fetch("/api/admin/hub-dash/live", { headers: authHeaders() }),
        fetch("/api/admin/item-delivery-requests?limit=100", { headers: authHeaders() }),
        fetch("/api/admin/drivers?limit=100", { headers: authHeaders() }),
        fetch("/api/admin/drivers/earnings-summary", { headers: authHeaders() }),
        fetch("/api/external-runs/admin/list", { headers: authHeaders() }),
      ]);

      if (ordersRes.ok) {
        const d = await ordersRes.json() as { items: LiveOrder[] };
        setLiveOrders(d.items ?? []);
      }
      if (hubRes.ok) {
        const d = await hubRes.json() as HubDashData;
        setHubDash(d);
      }
      if (itemRes.ok) {
        const d = await itemRes.json() as { requests: ItemRequest[] };
        setItemRequests(d.requests ?? []);
      }
      if (driversRes.ok) {
        const d = await driversRes.json() as { items: Driver[] };
        setDrivers(d.items ?? []);
      }
      if (summaryRes.ok) {
        const d = await summaryRes.json() as Summary;
        setSummary(d);
      }

      if(runsRes.ok)setRuns((await runsRes.json()).runs??[]);
      setConnected(true);
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
    countdownRef.current = REFRESH_INTERVAL;
    setCountdown(REFRESH_INTERVAL);
  }, []);

  // Initial load + refresh interval
  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), REFRESH_INTERVAL * 1000);
    return () => clearInterval(interval);
  }, [load]);

  // Countdown ticker
  useEffect(() => {
    const tick = setInterval(() => {
      countdownRef.current = Math.max(0, countdownRef.current - 1);
      setCountdown(countdownRef.current);
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  // Derived data
  const activeItemRequests = itemRequests.filter(r => ITEM_ACTIVE_STATUSES.has(r.status));
  const itemDispatching = activeItemRequests.filter(r => r.status === "dispatching" || r.status === "paid");
  const itemInTransit = activeItemRequests.filter(r => r.status === "accepted" || r.status === "picked_up");

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950 text-white flex flex-col overflow-hidden font-sans">
      {/* ── Main grid ── */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-3 text-slate-500">
            <RefreshCw size={20} className="animate-spin" />
            <span className="text-lg">Loading operations data…</span>
          </div>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-slate-800 overflow-y-auto lg:overflow-hidden">
          {/* Column 1: Food orders */}
          <div className="overflow-hidden">
            <FoodOrdersPanel orders={liveOrders} />
          </div>

          {/* Column 2: Dispatching */}
          <div className="overflow-hidden">
            <DispatchPanel
              foodDispatching={hubDash.dispatching}
              itemDispatching={itemDispatching}
              foodFailures={hubDash.failures}
              runs={runs.filter(r=>r.status==='dispatching')}
            />
          </div>

          {/* Column 3: In transit */}
          <div className="overflow-hidden">
            <InTransitPanel
              foodInTransit={hubDash.inTransit}
              itemInTransit={itemInTransit}
              runs={runs.filter(r=>['assigned','in_progress'].includes(r.status))}
            />
          </div>

          {/* Column 4: Drivers */}
          <div className="overflow-hidden">
            <DriversPanel drivers={drivers} />
          </div>
        </div>
      )}

      {/* ── Footer pulse ── */}
      <div className="flex-shrink-0 h-0.5 bg-slate-800">
        <div
          className="h-full bg-gradient-to-r from-violet-600 via-cyan-500 to-green-500 transition-none"
          style={{ width: `${((REFRESH_INTERVAL - countdown) / REFRESH_INTERVAL) * 100}%` }}
        />
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className="text-center">
      <div className={`text-xl font-bold leading-none ${color}`}>{value}</div>
      <div className="text-slate-500 text-xs mt-0.5 whitespace-nowrap">{label}</div>
    </div>
  );
}
